import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { IUser } from '../../../../../@types/IUser';
import { IAlbum } from '../../../../../@types/IAlbum';
import { ISoundtrack } from '../../../../../@types/ISoundtrack';
import SharedListHeader from '../SharedListHeader';
import { ReactComponent as BackArrow } from '../../../../../public/icons/back_arrow.svg';
import MediaFlatlist from '../../../../shared/MediaFlatlist';
import MediaFlatItem from '../../../../shared/MediaFlatitem';
import { getMiddleColumn } from '../../../../shared/viewsHelper';
import { showAlbum, indexAlbumMedia, removeMediumFromAlbum } from '../../../../../api/albums';
import hv_player_api from '../../../../../api/hv_player_api';
import { createReport } from '../../../../../api/reports';
import { isSelfId } from '../../../../../api/helper';

interface IProps {
  user: IUser;
}

function Album(props: IProps) {
  const [activeEventKey, setActiveEventKey] = useState(null);
  const [album, setAlbum] = useState<IAlbum | null>(null);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [mediaPage, setMediaPage] = useState(0);
  const [canLoadMore, setCanLoadMore] = useState(true);
  const [media, setMedia] = useState<ISoundtrack[]>([]);
  const { album_id }: any = useParams();

  const fetchAlbum = async () => {
    try {
      let {
        data: { album },
      } = await showAlbum(album_id);
      setAlbum(album);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchAlbumMedia = async () => {
    try {
      setLoadingMedia(true);
      let { data } = await indexAlbumMedia(album_id, {
        page_number: mediaPage,
      });
      setMedia([...media, ...data.media]);
      if (data.media.length < 15) {
        setCanLoadMore(false);
      } else {
        setMediaPage(mediaPage + 1);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchData = async () => {
    try {
      await fetchAlbum();
      await fetchAlbumMedia();
    } catch (error) {
      console.log(error);
    }
  };

  const handleScroll = (e: Event) => {
    const target = e.target as Element;
    if (target.scrollHeight - target.clientHeight - target.scrollTop < 80) {
      if (canLoadMore && !loadingMedia) {
        fetchAlbumMedia();
      }
    }
  };

  const onRemoveMedium = isSelfId(props.user.id)
    ? async (medium: ISoundtrack) => {
        try {
          await removeMediumFromAlbum(album_id, {
            album_medium: { medium_id: medium.id },
          });
          setMedia(media.filter((om) => om.id !== medium.id));
        } catch (error) {}
      }
    : () => null;

  const onReportMedium = async (medium: ISoundtrack) => {
    try {
      await createReport({
        report: {
          comment: 'This was offensive',
          reportable_id: medium.id,
          reportable_type: medium.type,
        },
      });
      alert('Reported succesfully');
    } catch (error) {
      alert(error.message);
    }
  };

  const mediumSetter = (medium: ISoundtrack) =>
    setMedia(media.map((om) => (medium.id === om.id ? medium : om)));

  useEffect(() => {
    getMiddleColumn()?.addEventListener('scroll', handleScroll);
    return () => getMiddleColumn()?.removeEventListener('scroll', handleScroll);
  }, [loadingMedia, canLoadMore]);

  useEffect(() => {
    fetchData();
  }, [album_id]);

  const backWardLink = `/${props.user.slug}/albums`;

  const onSelectEventKey = (eventKey: any) => setActiveEventKey(eventKey);

  const renderMedium = (medium: ISoundtrack, index: number) => (
    <MediaFlatItem
      key={medium.id.toString()}
      eventKey={medium.id.toString()}
      activeEventKey={activeEventKey}
      source={{ id: album?.id, type: album?.type }}
      sourceOwner={props.user}
      medium={medium}
      mediumSetter={mediumSetter}
      onRemoveMedium={onRemoveMedium}
      onReportMedium={onReportMedium}
      onRemoveFromQueue={hv_player_api.removeFromQueue}
      onAddToQueue={hv_player_api.addToQueue}
    />
  );

  const renderHeader = () => (
    <SharedListHeader list={album} setList={setAlbum} media={media} />
  );

  return (
    <div id="album" className="m-3">
      <Link to={backWardLink} className="position-absolute">
        <BackArrow className="my-2" />
      </Link>
      <MediaFlatlist
        className="mx-5 my-4"
        media={media}
        loadingMoreMedia={loadingMedia}
        onSelectEventKey={onSelectEventKey}
        renderMedium={renderMedium}
        renderHeader={renderHeader}
      />
    </div>
  );
}

export default Album;
